import { inject, Injectable } from '@angular/core';
import {
  selectAllEntities,
  selectEntity,
  setEntities,
  upsertEntities,
} from '@ngneat/elf-entities';
import { GroupService } from '@thiememeulenhoff/ng-api-gateway';
import { BehaviorSubject, map, switchMap, take } from 'rxjs';
import { Group } from '@thiememeulenhoff/types';
import { UserSettingFacade } from '../user-setting/user-setting.facade';
import * as stores from './group.store';

@Injectable({
  providedIn: 'root',
})
export class GroupFacade {
  readonly #groups = stores.groups;

  readonly #groupService = inject(GroupService);

  readonly #userSettingFacade = inject(UserSettingFacade);

  readonly groups$ = this.#groups.pipe(selectAllEntities());

  readonly #arrangementCode = new BehaviorSubject<string | null>(null);

  readonly #selectedGroupId = new BehaviorSubject<string>('');

  readonly selectedGroupId$ = this.#selectedGroupId.asObservable();

  readonly groupById$ = (id: string) => this.#groups.pipe(selectEntity(id));

  readonly groupName$ = (id: string) =>
    this.groupById$(id).pipe(map((group) => group?.name ?? ''));

  readonly selectedGroup$ = this.#selectedGroupId.pipe(
    switchMap((id) => this.groupById$(id)),
  );

  readonly validatedSelectedGroupId$ = this.selectedGroup$.pipe(
    map((group) => group?.id ?? ''),
  );

  setArrangementCodeAndMethodSlugAndFetch(
    arrangementCode: string,
    methodSlug: string,
  ) {
    if (this.#arrangementCode.value === arrangementCode) {
      return;
    }

    this.fetchGroups(methodSlug);

    this.#arrangementCode.next(arrangementCode);

    this.#userSettingFacade
      .getUserSetting(this.#getSelectedGroupSettingKey())
      .pipe(take(1))
      .subscribe((selectedGroup) => {
        this.#selectedGroupId.next(selectedGroup?.value ?? '');
      });
  }

  fetchGroups(scope: string, includeUserCount = false, allSchoolyears = false) {
    return this.#groupService
      .fetchGroups(scope, includeUserCount, allSchoolyears)
      .pipe(take(1))
      .subscribe((groupsIndex) => this.#setGroups(groupsIndex.items));
  }

  fetchGroup(id: string) {
    return this.#groupService
      .fetchGroup(id)
      .pipe(take(1))
      .subscribe((group) => this.#upsertGroup(group));
  }

  selectGroup(id: string) {
    if (
      this.#arrangementCode.value === null ||
      this.#selectedGroupId.value === id
    ) {
      return;
    }

    this.#selectedGroupId.next(id);
    this.#userSettingFacade
      .setUserSetting(
        {
          key: this.#getSelectedGroupSettingKey(),
          value: id,
        },
        true,
      )
      .pipe(take(1))
      .subscribe();
  }

  #getSelectedGroupSettingKey() {
    return `selected-group-${this.#arrangementCode.value ?? ''}`;
  }

  // #region Store mutations
  #setGroups = (groups: Array<Group>) => {
    this.#groups.update(setEntities(groups));
  };

  #upsertGroup = (group: Group) => {
    this.#groups.update(upsertEntities(group));
  };
  // #endregion
}
